import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { MessageCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import {
  trackFormStart, trackFormStepEventType, trackFormStepBudget,
  trackFormStepLocation, trackFormStepContactStarted, trackFormAbandon,
  trackFormSubmit, trackWhatsAppClick, trackConversion, trackContactPageView,
} from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WA_NUMBER = '918527043856';
const LOGO_URL = "/logo-new.png";

const EVENT_TYPES = [
  'Wedding Celebration',
  'Pre-Wedding / Intimate Function',
  'Private Celebration',
  'Brand / Corporate Experience',
  'Luxury Dining / Home Event',
  'Exhibition / Installation',
  'Something else',
];
const LOCATIONS = ['India', 'UAE', 'UK', 'USA', 'Other'];
const BUDGETS = [
  { value: 'under-5l', label: 'Under \u20B95L' },
  { value: '5l-10l', label: '\u20B95L \u2013 \u20B910L' },
  { value: '10l-25l', label: '\u20B910L \u2013 \u20B925L' },
  { value: '25l-50l', label: '\u20B925L \u2013 \u20B950L' },
  { value: '50l-1cr', label: '\u20B950L \u2013 \u20B91Cr' },
  { value: '1cr-plus', label: '\u20B91Cr+' },
  { value: 'prefer-not', label: 'Prefer not to say' },
];
const TOTAL_STEPS = 6;
const STEP_NAMES = ['', 'event_type', 'based_out_of', 'location', 'budget', 'message', 'contact'];
const ABANDON_TIMEOUT = 30000;
const WHATSAPP_NUDGE_TIMEOUT = 22000;

const slideVariants = {
  enter: { opacity: 0, x: 30 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
};
const inputClass = "w-full bg-transparent border-0 border-b border-white/15 text-white text-sm font-light tracking-wide py-3 px-0 outline-none placeholder:text-white/20 focus:border-white/50 transition-colors duration-300";

function OptionCard({ label, selected, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`w-full text-left px-5 py-3 rounded-lg border transition-all duration-250 text-[13px] font-light tracking-wide active:scale-[0.98] ${
        selected
          ? 'border-[#D8B4A0]/50 bg-white/10 text-white scale-[1.01]'
          : 'border-white/8 text-white/45 hover:border-white/20 hover:text-white/70 hover:scale-[1.01]'
      }`}>
      {label}
    </button>
  );
}

function buildWhatsAppUrl(form) {
  const eventType = form.custom_event_type || (form.event_type !== 'Something else' ? form.event_type : '');
  const location = form.event_location;
  const budgetLabel = BUDGETS.find(b => b.value === form.budget_range)?.label;

  // If nothing captured, use fallback
  if (!eventType && !location && !budgetLabel) {
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hi, I'm planning an event and would like to connect with CinCin.")}`;
  }

  const lines = [];
  if (eventType && location) {
    lines.push(`Hi, I'm planning a ${eventType} in ${location}.`);
  } else if (eventType) {
    lines.push(`Hi, I'm planning a ${eventType}.`);
  } else if (location) {
    lines.push(`Hi, I'm planning an event in ${location}.`);
  }
  if (budgetLabel && budgetLabel !== 'Prefer not to say') {
    lines.push(`\nEstimated budget: ${budgetLabel}.`);
  }
  lines.push("\nWould love to explore how CinCin can design this experience.");
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`;
}

export default function ContactSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });
  const trackedView = useRef(false);
  const formStarted = useRef(false);
  const contactTypingTracked = useRef(false);
  const abandonFired = useRef(false);
  const formSubmitted = useRef(false);
  const abandonTimer = useRef(null);
  const startTime = useRef(null);
  const [showWhatsAppNudge, setShowWhatsAppNudge] = useState(false);
  const nudgeTimer = useRef(null);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    event_type: '', custom_event_type: '', based_out_of: '',
    event_location: '', budget_range: '', message: '',
    name: '', email: '', phone: '',
  });

  if (isInView && !trackedView.current) {
    trackedView.current = true;
    trackContactPageView();
  }

  // Abandonment + nudge timer management
  const resetTimers = useCallback(() => {
    if (abandonTimer.current) clearTimeout(abandonTimer.current);
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);

    if (!formSubmitted.current && formStarted.current) {
      // WhatsApp nudge after 22s inactivity
      nudgeTimer.current = setTimeout(() => {
        if (!formSubmitted.current) setShowWhatsAppNudge(true);
      }, WHATSAPP_NUDGE_TIMEOUT);

      // Abandon event after 30s inactivity
      abandonTimer.current = setTimeout(() => {
        if (!abandonFired.current && !formSubmitted.current && formStarted.current) {
          abandonFired.current = true;
          const elapsed = startTime.current ? Math.round((Date.now() - startTime.current) / 1000) : 0;
          trackFormAbandon(STEP_NAMES[step] || 'unknown', elapsed);
        }
      }, ABANDON_TIMEOUT);
    }
  }, [step]);

  // Reset timers on any interaction
  const onInteract = useCallback(() => {
    setShowWhatsAppNudge(false);
    resetTimers();
    if (!formStarted.current) {
      formStarted.current = true;
      startTime.current = Date.now();
      trackFormStart();
    }
  }, [resetTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abandonTimer.current) clearTimeout(abandonTimer.current);
      if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
    };
  }, []);

  const set = useCallback((key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    onInteract();
  }, [onInteract]);

  const selectAndAdvance = useCallback((key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    onInteract();
    // Fire step-specific events
    if (key === 'event_type') trackFormStepEventType(val);
    if (key === 'budget_range') trackFormStepBudget(val);
    if (key === 'based_out_of') {
      // based_out_of doesn't have its own event, but we track via form_start
    }
    setTimeout(() => setStep(s => s + 1), 250);
  }, [onInteract]);

  const canNext = () => {
    if (step === 1) return form.event_type !== '';
    if (step === 2) return form.based_out_of !== '';
    if (step === 6) return form.name && form.email;
    return true;
  };

  const next = () => {
    if (!canNext() || step >= TOTAL_STEPS) return;
    onInteract();
    if (step === 3) trackFormStepLocation();
    setStep(s => s + 1);
  };
  const prev = () => { if (step > 1) { onInteract(); setStep(s => s - 1); } };

  const handleContactInput = useCallback((key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    onInteract();
    if (!contactTypingTracked.current) {
      contactTypingTracked.current = true;
      trackFormStepContactStarted();
    }
  }, [onInteract]);

  const handleSubmit = async () => {
    if (!form.name || !form.email) return;
    setSubmitting(true);
    formSubmitted.current = true;
    if (abandonTimer.current) clearTimeout(abandonTimer.current);
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name, email: form.email, phone: form.phone,
        event_type: form.event_type, custom_event_type: form.custom_event_type,
        based_out_of: form.based_out_of, event_location: form.event_location,
        budget_range: form.budget_range, message: form.message,
      });
      setSubmitted(true);
      trackFormSubmit();
      trackConversion('generate_lead');
    } catch (err) {
      console.error('Submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsApp = (source) => {
    onInteract();
    trackWhatsAppClick(source, STEP_NAMES[step] || 'unknown');
    trackConversion('whatsapp_click');
  };

  const isManualStep = step === 3 || step === 5 || step === 6
    || (step === 1 && form.event_type === 'Something else');

  const waUrl = buildWhatsAppUrl(form);

  return (
    <section id="contact" data-testid="contact-section" ref={sectionRef}
      className="relative py-12 md:py-20 px-6 md:px-16 lg:px-24 overflow-hidden">

      <div className="absolute inset-0 z-0">
        <img src="/portfolio/drive_wedding_8.jpg" alt="" className="w-full h-full object-cover"
          style={{ filter: 'blur(16px)', transform: 'scale(1.1)' }} loading="lazy" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.68))' }} />
      </div>

      <div className="relative z-10 max-w-[480px] mx-auto">
        <div className="rounded-xl px-5 py-8 md:px-9 md:py-10"
          style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>

        {/* Compressed Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }} className="text-center mb-6">
          <span className="text-[9px] tracking-[0.25em] uppercase text-[#EAEAEA]/40 font-light">Get in Touch</span>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#FFFFFF] tracking-tight mt-2 leading-[1.1]"
            data-testid="contact-headline">
            Let's create something unforgettable.
          </h2>
          <p className="mt-2 font-light text-[11px] sm:text-[12px] text-[#EAEAEA]/40 tracking-wide">
            Planning from India or anywhere in the world, we'll guide you every step.
          </p>
        </motion.div>

        {!submitted ? (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }} data-testid="contact-form">

            {/* Progress */}
            <div className="flex gap-1.5 mb-6">
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div key={i} className={`h-[2px] flex-1 rounded-full transition-all duration-500 ${i < step ? 'bg-white/40' : 'bg-white/8'}`} />
              ))}
            </div>

            <div className="min-h-[240px]">
              <AnimatePresence mode="wait">

                {step === 1 && (
                  <motion.div key="s1" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#EAEAEA]/35 font-light mb-4">What are you planning?</p>
                    <div className="space-y-2">
                      {EVENT_TYPES.map(t => (
                        <OptionCard key={t} label={t} selected={form.event_type === t}
                          onClick={() => t === 'Something else' ? set('event_type', t) : selectAndAdvance('event_type', t)}
                          testId={`event-type-${t.toLowerCase().replace(/[^a-z]/g, '-')}`} />
                      ))}
                    </div>
                    {form.event_type === 'Something else' && (
                      <input type="text" value={form.custom_event_type}
                        onChange={e => set('custom_event_type', e.target.value)}
                        placeholder="Describe your event" className={`${inputClass} mt-3`} data-testid="custom-event-input" />
                    )}
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div key="s2" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#EAEAEA]/35 font-light mb-4">Where are you based?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {LOCATIONS.map(l => (
                        <OptionCard key={l} label={l} selected={form.based_out_of === l}
                          onClick={() => selectAndAdvance('based_out_of', l)}
                          testId={`location-${l.toLowerCase()}`} />
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div key="s3" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#EAEAEA]/35 font-light mb-4">Where will the event be?</p>
                    <input type="text" value={form.event_location}
                      onChange={e => set('event_location', e.target.value)}
                      placeholder="City, destination, or venue (if decided)" className={inputClass} data-testid="event-location-input" />
                  </motion.div>
                )}

                {step === 4 && (
                  <motion.div key="s4" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#EAEAEA]/35 font-light mb-4">What scale are you imagining?</p>
                    <div className="space-y-2">
                      {BUDGETS.map(b => (
                        <OptionCard key={b.value} label={b.label} selected={form.budget_range === b.value}
                          onClick={() => selectAndAdvance('budget_range', b.value)}
                          testId={`budget-${b.value}`} />
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === 5 && (
                  <motion.div key="s5" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#EAEAEA]/35 font-light mb-1">Tell us what you're imagining</p>
                    <p className="text-[10px] text-white/15 font-light mb-4">Even if it's just a feeling, a reference, or a starting point.</p>
                    <textarea value={form.message} onChange={e => set('message', e.target.value)}
                      placeholder="Share your vision..." rows={4} className={`${inputClass} resize-none`} data-testid="message-textarea" />
                  </motion.div>
                )}

                {step === 6 && (
                  <motion.div key="s6" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#EAEAEA]/35 font-light mb-4">Your details</p>
                    <div className="space-y-4">
                      <input type="text" value={form.name} onChange={e => handleContactInput('name', e.target.value)}
                        placeholder="Name" required className={inputClass} data-testid="contact-name-input" />
                      <input type="email" value={form.email} onChange={e => handleContactInput('email', e.target.value)}
                        placeholder="Email" required className={inputClass} data-testid="contact-email-input" />
                      <input type="tel" value={form.phone} onChange={e => handleContactInput('phone', e.target.value)}
                        placeholder="Phone (with country code)" className={inputClass} data-testid="contact-phone-input" />
                    </div>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>

            {/* Navigation */}
            <div className="mt-6 flex items-center justify-between">
              {step > 1 ? (
                <button type="button" onClick={prev} data-testid="form-back-btn"
                  className="flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-white/25 font-light hover:text-white/50 transition-colors">
                  <ArrowLeft size={12} /> Back
                </button>
              ) : <div />}

              {isManualStep && step < TOTAL_STEPS && (
                <button type="button" onClick={next} disabled={!canNext()} data-testid="form-next-btn"
                  className="flex items-center gap-1.5 px-7 py-2.5 rounded-full border border-white/20 text-white text-[10px] tracking-[0.18em] uppercase font-light hover:bg-white hover:text-[#0A0A0A] transition-all duration-400 disabled:opacity-15">
                  Continue <ArrowRight size={12} />
                </button>
              )}

              {step === TOTAL_STEPS && (
                <button type="button" onClick={handleSubmit} disabled={submitting || !canNext()} data-testid="contact-submit-button"
                  className="px-9 py-3 rounded-full border border-white/30 text-white text-[11px] tracking-[0.2em] uppercase font-light hover:bg-white hover:text-[#0A0A0A] transition-all duration-500 disabled:opacity-25">
                  {submitting ? 'Sending...' : 'Plan Your Event'}
                </button>
              )}
            </div>

            {/* WhatsApp — always visible + nudge on inactivity */}
            <AnimatePresence>
              {showWhatsAppNudge && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="mt-5 text-center py-3 rounded-lg border border-[#D8B4A0]/15 bg-white/[0.03]">
                  <p className="text-[10px] text-[#D8B4A0]/60 font-light tracking-wide mb-2">
                    Would you prefer a direct conversation?
                  </p>
                  <a href={waUrl} target="_blank" rel="noopener noreferrer"
                    onClick={() => handleWhatsApp('form_nudge')} data-testid="whatsapp-nudge-btn"
                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-[#D8B4A0]/20 text-[#D8B4A0]/50 text-[9px] tracking-[0.14em] uppercase font-light hover:border-[#D8B4A0]/40 hover:text-[#D8B4A0]/80 transition-all">
                    <MessageCircle size={11} strokeWidth={1.3} /> Continue on WhatsApp
                  </a>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-5 text-center">
              <p className="text-white/12 text-[9px] tracking-[0.18em] uppercase font-light mb-2">Prefer to speak directly?</p>
              <a href={waUrl} target="_blank" rel="noopener noreferrer"
                onClick={() => handleWhatsApp('form_footer')} data-testid="whatsapp-contact-button"
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-white/8 text-white/30 text-[9px] tracking-[0.14em] uppercase font-light hover:border-white/20 hover:text-white/50 transition-all duration-400">
                <MessageCircle size={11} strokeWidth={1.3} /> Continue on WhatsApp
              </a>
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }} className="text-center py-10" data-testid="contact-success-message">
            <motion.img src={LOGO_URL} alt="CinCin Events"
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="h-16 md:h-20 w-auto mx-auto mb-8 brightness-0 invert" />
            <p className="font-serif text-xl sm:text-2xl text-[#FFFFFF] leading-[1.3]">
              Your experience begins here.
            </p>
            <p className="mt-4 text-[13px] font-light text-[#EAEAEA]/50 leading-[1.8] tracking-wide max-w-[340px] mx-auto">
              We've received your details.<br />
              Our team will personally connect with you to begin crafting something exceptional.
            </p>
            <p className="mt-4 text-[10px] text-white/20 font-light tracking-wide">
              Typically within a few hours.
            </p>
          </motion.div>
        )}
        </div>
      </div>
    </section>
  );
}
